import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop/index.js';
import { AppAlertHost } from './utils/appAlert';
import Navbar from './components/Navbar/index.js';
import { CartProvider } from './context/CartContext';
import Home from './pages/Home/index.js'; // Keep Home eager for LCP
import { AuthProvider } from './context/AuthContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import { PatientAuthProvider } from './context/PatientAuthContext';
import FloatingCart from './components/FloatingCart/index.js';
import FlyToCartOverlay from './components/FlyToCartOverlay/index.js';
import Footer from './components/Footer/index.js';
import NotFoundModal from './components/NotFoundModal/index.js';
import AppDownloadPromo from './components/AppDownloadPromo/index.js';
import CookieConsentModal from './components/CookieConsent/CookieConsentModal.js';
import StaffPortalLogin from './pages/Auth/StaffPortalLogin.js';
import ProtectedRoute from './components/ProtectedRoute/index.js';
import SuperAdminRoute from './components/ProtectedRoute/SuperAdminRoute.js';

// Lazy load heavy portal and admin pages
const TestCatalog = lazy(() => import('./pages/TestCatalog/index.js'));
const Booking = lazy(() => import('./pages/Booking/index.js'));
const BookingCheckoutSuccess = lazy(() => import('./pages/BookingCheckoutSuccess/index.js'));
const OffersHub = lazy(() => import('./pages/OffersHub/index.js'));
// eslint-disable-next-line no-unused-vars
const EmployerHub = lazy(() => import('./pages/EmployerHub/index.js'));
const ResearchCentralLab = lazy(() => import('./pages/ResearchCentralLab/index.js'));

const Cart = lazy(() => import('./pages/Cart/index.js'));
const PlaceholderPage = lazy(() => import('./pages/PlaceholderPage/index.js'));
const PublicCMSPage = lazy(() => import('./pages/PublicCMSPage'));
const EmployerDashboard = lazy(() => import('./pages/EmployerDashboard/index.js'));
const SuperAdminLayout = lazy(() => import('./components/Admin/SuperAdminLayout.js'));
const SuperAdminLogin = lazy(() => import('./pages/SuperAdmin/Login.js'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdmin/Dashboard.js'));
const OffersEngine = lazy(() => import('./pages/SuperAdmin/OffersEngine.js'));
const CatalogManager = lazy(() => import('./pages/SuperAdmin/CatalogManager.js'));
const PhlebotomyManagement = lazy(() => import('./pages/SuperAdmin/PhlebotomyManagement.js'));
const PayoutManager = lazy(() => import('./pages/SuperAdmin/PayoutManager.js'));
const SettingsManager = lazy(() => import('./pages/SuperAdmin/SettingsManager.js'));
const LISManager = lazy(() => import('./pages/SuperAdmin/LISManager.js'));
const LabsManager = lazy(() => import('./pages/SuperAdmin/LabsManager.js'));
const UsersManager = lazy(() => import('./pages/SuperAdmin/UsersManager.js'));
const ReviewsManager = lazy(() => import('./pages/SuperAdmin/ReviewsManager.js'));
const AnalyticsDashboard = lazy(() => import('./pages/SuperAdmin/AnalyticsDashboard.js'));
const ActivityLog = lazy(() => import('./pages/SuperAdmin/ActivityLog.js'));
const AdminInvitationsManager = lazy(() => import('./pages/SuperAdmin/AdminInvitationsManager.js'));
const AcceptAdminInvite = lazy(() => import('./pages/SuperAdmin/AcceptAdminInvite.js'));
const AcceptInvitation = lazy(() => import('./pages/AcceptInvitation/AcceptInvitation.js'));
const CMSManager = lazy(() => import('./pages/SuperAdmin/CMSManager'));
const FAQManager = lazy(() => import('./pages/SuperAdmin/FAQManager.js'));
const FAQPage = lazy(() => import('./pages/FAQ/index.js'));
const PartnershipsPage = lazy(() => import('./pages/Partnerships/index.js'));
const AppCMSManager = lazy(() => import('./pages/SuperAdmin/AppCMSManager'));
const NewsletterTab = lazy(() => import('./pages/Admin/NewsletterTab.js'));
// eslint-disable-next-line no-unused-vars
const PortalsManager = lazy(() => import('./pages/SuperAdmin/PortalsManager.js'));
const EmployeeEnrollment = lazy(() => import('./pages/EmployeeEnrollment/index.js'));
const MobilePhlebotomy = lazy(() => import('./pages/MobilePhlebotomy/index.js'));
const AssistedLiving = lazy(() => import('./pages/AssistedLiving/index.js'));
const CommunityPrograms = lazy(() => import('./pages/CommunityPrograms/index.js'));
const PhysicianPortal = lazy(() => import('./pages/PhysicianPortal/index.js'));
const EarlyDiagnostics = lazy(() => import('./pages/EarlyDiagnostics/index.js'));
const SelfPayPatients = lazy(() => import('./pages/SelfPay/index.js'));
const PhlebotomistDashboard = lazy(() => import('./pages/MobilePhlebotomy/Dashboard.js'));
const PhlebotomistResetPassword = lazy(() => import('./pages/MobilePhlebotomy/ResetPassword.js'));

const PhlebotomyHub = lazy(() => import('./pages/PhlebotomyHub/index'));

const LabOwnerDashboard = lazy(() => import('./pages/LabOwner/LabOwnerDashboard.js'));
const DiagnosticDashboard = lazy(() => import('./pages/DiagnosticPortal/DiagnosticDashboard.js'));
const LabManagerDashboard = lazy(() => import('./pages/LabManager/LabManagerDashboard.js'));
const WorkerSignup = lazy(() => import('./pages/PortalAuth/WorkerSignup.js'));
const DoctorPortalDashboard = lazy(() => import('./pages/DoctorPortal/DoctorPortalDashboard.js'));
const FacilityPortalDashboard = lazy(() => import('./pages/FacilityPortal/FacilityPortalDashboard.js'));
const NonprofitPortalDashboard = lazy(() => import('./pages/NonprofitPortal/NonprofitPortalDashboard.js'));
const TechnicianDashboard = lazy(() => import('./pages/TechnicianPortal/TechnicianDashboard.js'));
const SalesDashboard = lazy(() => import('./pages/SalesPortal/SalesDashboard.js'));
const PatientAuth = lazy(() => import('./pages/MobilePhlebotomy/PatientPortal/Auth.js'));
const BookingWizard = lazy(() => import('./pages/MobilePhlebotomy/PatientPortal/BookingWizard.js'));
const PatientDashboard = lazy(() => import('./pages/MobilePhlebotomy/PatientPortal/PatientDashboard.js'));
const DeleteAccountPage = lazy(() => import('./pages/MobilePhlebotomy/PatientPortal/DeleteAccountPage.js'));
const PatientTracking = lazy(() => import('./pages/MobilePhlebotomy/PatientPortal/Tracking.js'));
const PhlebotomistProfilePage = lazy(() => import('./pages/MobilePhlebotomy/PatientPortal/PhlebotomistProfile.js'));
const PatientRating = lazy(() => import('./pages/MobilePhlebotomy/PatientPortal/RatingScreen.js'));
const ContactPage = lazy(() => import('./pages/Contact/index.js'));
const SpecialistOnboarding = lazy(() => import('./pages/MobilePhlebotomy/SpecialistPortal/Onboarding.js'));
const OnsiteVisit = lazy(() => import('./pages/OnsiteVisit/index.js'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe/index.js'));

// Dispatch System Pages
const DispatchBooking = lazy(() => import('./pages/Dispatch/DispatchBooking.js'));
const BookingStatus = lazy(() => import('./pages/Dispatch/BookingStatus.js'));
const DispatchPortal = lazy(() => import('./pages/Dispatch/PhlebPortal/DispatchPortal.js'));
const DispatchAdmin = lazy(() => import('./pages/Dispatch/AdminDashboard/DispatchAdmin.js'));

const DispatchManager = lazy(() => import('./pages/SuperAdmin/DispatchManager.js'));

// Loading Fallback
const PageLoader = () => (
  <div className="page-loader-minimal">
    <div className="loader-orbit"></div>
  </div>
);

// Layout component for public-facing site
const PublicLayout = ({ children }) => (
  <div className="app-container">
    <Navbar />
    <main>
      {children}
    </main>
    <Footer />
    <FloatingCart />
    <FlyToCartOverlay />
  </div>
);

const LoginRedirect = () => {
  const location = useLocation();
  return <Navigate to={{ pathname: '/portal/login', search: location.search }} replace />;
};

function App() {
  return (
    <Router future={{ 
      v7_startTransition: true, 
      v7_relativeSplatPath: true 
    }}>
      <AdminAuthProvider>
        <PatientAuthProvider>
          <AuthProvider>
          <CartProvider>
            <ScrollToTop />
            <AppAlertHost />
            <AppDownloadPromo />
            <CookieConsentModal />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Super Admin Routes (Shared Layout) */}
                <Route path="/superadmin/login" element={<SuperAdminLogin />} />
                <Route path="/admin-invite/accept" element={<AcceptAdminInvite />} />
                <Route path="/superadmin" element={
                  <SuperAdminRoute>
                    <SuperAdminLayout />
                  </SuperAdminRoute>
                }>
                  <Route index element={<Navigate to="/superadmin/dashboard" replace />} />
                  <Route path="dashboard" element={<SuperAdminDashboard />} />
                  <Route path="identity" element={<AdminInvitationsManager />} />
                  <Route path="invitations" element={<AdminInvitationsManager />} />
                  <Route path="cms" element={<CMSManager />} />
                  <Route path="faqs" element={<FAQManager />} />
                  <Route path="app-cms" element={<AppCMSManager />} />
                  <Route path="commerce" element={<CatalogManager />} />
                  <Route path="catalog" element={<CatalogManager />} />
                  <Route path="reviews" element={<ReviewsManager />} />
                  <Route path="analytics" element={<AnalyticsDashboard />} />
                  <Route path="offers" element={<OffersEngine />} />
                  <Route path="bookings" element={<Navigate to="/superadmin/payouts" replace />} />
                  <Route path="dispatch" element={<DispatchManager />} />
                  <Route path="payouts" element={<PayoutManager />} />
                  <Route path="phlebotomy/*" element={<PhlebotomyManagement />} />
                  <Route path="settings" element={<SettingsManager />} />
                  <Route path="lis" element={<LISManager />} />
                  <Route path="labs" element={<LabsManager />} />
                  <Route path="integrations" element={<PlaceholderPage title="Integrations" />} />
                  <Route path="crm" element={<NewsletterTab />} />
                  <Route path="newsletter" element={<NewsletterTab />} />
                  <Route path="portals" element={<UsersManager />} />
                  <Route path="users" element={<UsersManager />} />
                  <Route path="activity-log" element={<ActivityLog />} />
                </Route>
                <Route path="/super-admin/*" element={<Navigate to="/superadmin/dashboard" replace />} />


                {/* Portal Routes */}
                <Route path="/portal/employer" element={
                  <ProtectedRoute>
                    <EmployerDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/employer/dashboard" element={<Navigate to="/portal/employer" replace />} />
                <Route path="/portal/diagnostic/*" element={<DiagnosticDashboard />} />
                <Route path="/portal/phlebotomist/dashboard" element={<PhlebotomistDashboard />} />
                <Route path="/portal/phlebotomist/reset-password" element={<PhlebotomistResetPassword />} />
                <Route path="/portal/phlebotomy-hub/*" element={<PhlebotomyHub />} />
                <Route path="/hub/dashboard" element={<Navigate to="/portal/phlebotomy-hub" replace />} />
                <Route path="/hub/*" element={<Navigate to="/portal/phlebotomy-hub" replace />} />
                <Route path="/login" element={<LoginRedirect />} />
                <Route path="/signin" element={<LoginRedirect />} />
                <Route path="/sign-in" element={<LoginRedirect />} />
                <Route path="/portal/login" element={<PatientAuth />} />
                <Route path="/portal/patient/book" element={<BookingWizard />} />
                {/* Unified booking entry - same full-screen wizard as the portal
                    route (outside PublicLayout: it's its own page chrome, not
                    site content). Handles guests via its built-in gate, so this
                    replaces the old, separately-maintained /book stepper. */}
                <Route path="/book" element={<BookingWizard />} />
                <Route path="/phleb/booking" element={<BookingWizard />} />
                <Route path="/portal/patient/dashboard" element={<PatientDashboard />} />
                <Route path="/portal/patient/delete-account" element={<DeleteAccountPage />} />
                <Route path="/portal/patient/reset-password" element={<PhlebotomistResetPassword />} />
                <Route path="/portal/reset-password" element={<PhlebotomistResetPassword />} />
                <Route path="/portal/patient/tracking" element={<PatientTracking />} />
                <Route path="/portal/patient/phlebotomist/:id" element={<PhlebotomistProfilePage />} />
                <Route path="/portal/patient/rate" element={<PatientRating />} />
                {/* Enterprise Dedicated Portal URLs (Lab Owner, Lab Manager, Technician, Doctor) */}
                <Route path="/portal/lab-owner/dashboard" element={<LabOwnerDashboard />} />
                <Route path="/portal/lab-manager/dashboard" element={<LabManagerDashboard />} />
                <Route path="/portal/lab-manager/signup" element={<Navigate to="/staff/login" replace />} />
                <Route path="/portal/register" element={<WorkerSignup />} />
                <Route path="/portal/doctor/dashboard" element={<DoctorPortalDashboard />} />
                <Route path="/portal/doctor" element={<Navigate to="/portal/doctor/dashboard" replace />} />
                <Route path="/portal/physician/dashboard" element={<DoctorPortalDashboard />} />
                <Route path="/portal/physician" element={<Navigate to="/portal/physician/dashboard" replace />} />
                <Route path="/portal/facility/dashboard" element={<FacilityPortalDashboard />} />
                <Route path="/portal/facility" element={<Navigate to="/portal/facility/dashboard" replace />} />
                <Route path="/portal/nonprofit/dashboard" element={<NonprofitPortalDashboard />} />
                <Route path="/portal/nonprofit" element={<Navigate to="/portal/nonprofit/dashboard" replace />} />
                <Route path="/portal/technician/dashboard" element={<TechnicianDashboard />} />
                {/* LIS Enterprise Portal (Full-Screen Dedicated Workspace) */}
                <Route path="/portal/lis" element={
                  <SuperAdminRoute>
                    <LISManager isStandalonePortal={true} />
                  </SuperAdminRoute>
                } />
                <Route path="/portal/sales/dashboard" element={<SalesDashboard />} />
                <Route path="/portal/staff/login" element={<Navigate to="/staff/login" replace />} />
                <Route path="/portal/staff" element={<Navigate to="/staff/login" replace />} />
                <Route path="/staff/login" element={<StaffPortalLogin />} />
                <Route path="/staff" element={<Navigate to="/staff/login" replace />} />
                <Route path="/portal/lab-manager/login" element={<Navigate to="/staff/login" replace />} />
                <Route path="/portal/lab-owner/login" element={<Navigate to="/staff/login" replace />} />
                <Route path="/portal/technician/login" element={<Navigate to="/staff/login" replace />} />
                <Route path="/portal/doctor/login" element={<Navigate to="/staff/login" replace />} />
                <Route path="/portal/physician/login" element={<Navigate to="/staff/login" replace />} />
                <Route path="/portal/facility/login" element={<Navigate to="/staff/login" replace />} />
                <Route path="/portal/nonprofit/login" element={<Navigate to="/staff/login" replace />} />
                <Route path="/portal/lab/login" element={<Navigate to="/staff/login" replace />} />
                <Route path="/portal/partner/login" element={<Navigate to="/staff/login" replace />} />
                <Route path="/mobile-phlebotomy/onboarding" element={<SpecialistOnboarding />} />
                <Route path="/enroll/:token" element={<EmployeeEnrollment />} />
                <Route path="/accept-invitation" element={<AcceptInvitation />} />
                <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />

                {/* Legacy Portal Login Aliases */}
                <Route path="/portal/specialist/login" element={<Navigate to="/portal/login?portal=specialist" replace />} />
                <Route path="/portal/hub/login" element={<Navigate to="/portal/login?portal=hub" replace />} />
                <Route path="/portal/research/login" element={<Navigate to="/portal/login?portal=research" replace />} />
                <Route path="/portal/admin/login" element={<Navigate to="/superadmin/login" replace />} />

                {/* Dispatch System Routes */}
                <Route path="/dispatch/book" element={<DispatchBooking />} />
                <Route path="/dispatch/status/:bookingId" element={<BookingStatus />} />
                <Route path="/dispatch/portal/*" element={<DispatchPortal />} />
                <Route path="/dispatch/admin/*" element={
                  <SuperAdminRoute>
                    <DispatchAdmin />
                  </SuperAdminRoute>
                } />

                {/* Public Site Routes */}
                <Route path="*" element={
                  <PublicLayout>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/tests" element={<TestCatalog />} />
                      <Route path="/test-catalog" element={<TestCatalog />} />
                      <Route path="/catalog" element={<Navigate to="/tests" replace />} />
                      {/* Walk-in / onsite (center) booking - distinct service
                          from mobile (/book → BookingWizard): no collection fee,
                          self-pay shows test prices, insured shows no price. */}
                      <Route path="/onsite-book" element={<Booking />} />
                      <Route path="/book/success" element={<BookingCheckoutSuccess />} />
                      <Route path="/offers" element={<OffersHub />} />
                      <Route path="/cart" element={<Cart />} />
                      <Route path="/employer-health-program" element={<EmployerHub />} />
                      <Route path="/about" element={<PlaceholderPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/locations" element={<PlaceholderPage />} />
                      <Route path="/panels" element={<Navigate to="/tests" replace />} />
                      <Route path="/pricing" element={<Navigate to="/tests" replace />} />
                      <Route path="/blog" element={<PlaceholderPage />} />
                      <Route path="/login" element={<LoginRedirect />} />
                      <Route path="/signin" element={<LoginRedirect />} />
                      <Route path="/sign-in" element={<LoginRedirect />} />
                      <Route path="/privacy" element={<PublicCMSPage />} />
                      <Route path="/cookie-policy" element={<PublicCMSPage />} />
                      <Route path="/cookies" element={<PublicCMSPage />} />
                      <Route path="/terms" element={<PublicCMSPage />} />
                      <Route path="/research-central-lab" element={<ResearchCentralLab />} />
                      <Route path="/mobile-phlebotomy" element={<MobilePhlebotomy />} />
                      <Route path="/assisted-living" element={<AssistedLiving />} />
                      <Route path="/assisted-living-testing" element={<AssistedLiving />} />
                      <Route path="/facilities" element={<AssistedLiving />} />
                      <Route path="/senior-living" element={<AssistedLiving />} />
                      <Route path="/community-programs" element={<CommunityPrograms />} />
                      <Route path="/physicians" element={<PhysicianPortal />} />
                      <Route path="/early-diagnostics" element={<EarlyDiagnostics />} />
                      <Route path="/self-pay-lab-tests" element={<SelfPayPatients />} />
                      <Route path="/onsite-visit" element={<OnsiteVisit />} />
                      <Route path="/faq" element={<FAQPage />} />
                      <Route path="/faqs" element={<FAQPage />} />
                      <Route path="/hipaa" element={<PublicCMSPage />} />
                      <Route path="/refunds" element={<PublicCMSPage />} />
                      <Route path="/refund-policy" element={<PublicCMSPage />} />
                      <Route path="/certifications" element={<PublicCMSPage />} />
                      <Route path="/partnerships" element={<PartnershipsPage />} />
                      <Route path="/unsubscribe" element={<Unsubscribe />} />
                      <Route path="*" element={<NotFoundModal />} />
                    </Routes>
                  </PublicLayout>
                } />
              </Routes>
            </Suspense>
          </CartProvider>
        </AuthProvider>
      </PatientAuthProvider>
    </AdminAuthProvider>
    </Router>
  );
}
export default App;
